'use client'

import { useEffect, useState } from 'react'

import { dismissAfterMs, TOAST_ACCENT, TOAST_DOT, type Toast, toastRole } from '@/lib/toast'

/**
 * One transient message.
 *
 * Two behaviours here are accessibility requirements rather than polish:
 *
 *   • the ROLE depends on the kind — an error interrupts (`alert`), a
 *     confirmation waits its turn (`status`);
 *   • the TIMER stops while the card is hovered or focused. A message that
 *     disappears while it is being read, or while somebody is tabbing towards
 *     its close button, is WCAG 2.2.1's exact complaint — and the close button
 *     was unreachable in practice, because 3.5 seconds is less time than it
 *     takes to tab to it.
 *
 * Errors have no timer at all (see `dismissAfterMs`), so for them the pause is
 * moot and the button is the way out.
 */
export function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [held, setHeld] = useState(false)
  const lifetime = dismissAfterMs(toast.kind)

  useEffect(() => {
    if (lifetime === null || held) return undefined
    const timer = setTimeout(onDismiss, lifetime)
    return () => clearTimeout(timer)
  }, [lifetime, held, onDismiss])

  return (
    <div
      role={toastRole(toast.kind)}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
      onBlurCapture={() => setHeld(false)}
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-2xl border ${TOAST_ACCENT[toast.kind]} px-4 py-3 text-white shadow-[0_18px_60px_-24px_rgba(0,0,0,0.7)] backdrop-blur-xl`}
      style={{ animation: 'flou-toast-in 280ms cubic-bezier(0.2, 0.7, 0.2, 1)' }}
    >
      <span
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${TOAST_DOT[toast.kind]}`}
        aria-hidden
      />
      <div className="flex-1 text-[0.95rem] leading-snug">
        <div className="font-semibold">{toast.title}</div>
        {toast.description ? <div className="mt-0.5 text-white/65">{toast.description}</div> : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Zamknij"
        className="-mr-1 -mt-1 rounded-full p-1 text-white/55 transition-colors hover:bg-white/10 hover:text-white"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  )
}
